import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchVariants,
  fetchVariantsByProductIds,
  fetchLocations,
  bulkSetInventory,
  updateInventory,
  type VariantsParams,
} from '@/lib/api';
import { invalidateCatalogQueries } from './catalog-keys';

/** Paginated list of variants — one row per variant. Powers the Variants page. */
export function useVariants(params: VariantsParams = {}) {
  return useQuery({
    queryKey: ['variants', params],
    queryFn: () => fetchVariants(params),
    placeholderData: (prev) => prev,
    staleTime: 10_000,
  });
}

/**
 * Every variant of one product — lazily loaded when a product row on the
 * Products list is expanded. `enabled` is false until the row opens.
 */
export function useProductVariants(shopifyProductId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['product-variants', shopifyProductId],
    queryFn: () => fetchVariantsByProductIds([shopifyProductId!]),
    enabled: enabled && shopifyProductId != null,
    staleTime: 10_000,
  });
}

/** Store locations, for scoping inventory edits. Rarely changes — cached long. */
export function useLocations() {
  return useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
    staleTime: 5 * 60_000,
  });
}

/** Set stock for one variant at one location, then push to Shopify. */
export function useUpdateInventory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      inventoryItemId,
      available,
      locationId,
    }: {
      inventoryItemId: number;
      available: number;
      locationId: number;
    }) => updateInventory(inventoryItemId, { available, location_id: locationId }),
    onSuccess: () => {
      invalidateCatalogQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

/** Bulk-set stock on many variants at one location, then push to Shopify. */
export function useBulkSetInventory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      items,
      locationId,
      available,
    }: {
      items: { shopify_inventory_item_id: number; shopify_variant_id: number }[];
      locationId: number;
      available: number;
    }) => bulkSetInventory(items, locationId, available),
    onSuccess: () => {
      invalidateCatalogQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}
